import { supabase } from '@/lib/supabase-client';
export const strategyMapService = {
    /**
     * Fetch all goals for the strategy map with optional filtering
     */
    async getStrategyGoals(filter) {
        try {
            let query = supabase
                .from('goals')
                .select('*');
            if (filter.organizationId) {
                query = query.eq('organization_id', filter.organizationId);
            }
            if (filter.periodStart && filter.periodEnd) {
                query = query
                    .gte('time_period_end', filter.periodStart.toISOString().split('T')[0])
                    .lte('time_period_start', filter.periodEnd.toISOString().split('T')[0]);
            }
            if (filter.status) {
                query = query.eq('status', filter.status);
            }
            if (filter.ownerId) {
                query = query.eq('owner_id', filter.ownerId);
            }
            const { data, error } = await query;
            if (error) {
                console.error('Error fetching strategy goals:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch strategy goals:', error);
            return [];
        }
    },
    /**
     * Get goal relationships for visualization
     */
    async getGoalRelationships(organizationId) {
        try {
            const { data, error } = await supabase
                .from('goal_relationships')
                .select(`
          id,
          parent_goal_id,
          child_goal_id,
          relationship_type,
          created_at
        `)
                .in('parent_goal_id', (await supabase
                .from('goals')
                .select('id')
                .eq('organization_id', organizationId))
                .data?.map(g => g.id) || []);
            if (error) {
                console.error('Error fetching goal relationships:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch goal relationships:', error);
            return [];
        }
    },
    /**
     * Update goal canvas position
     */
    async updateGoalPosition(goalId, x, y) {
        try {
            const { error } = await supabase
                .from('goals')
                .update({
                canvas_x: x,
                canvas_y: y,
                updated_at: new Date().toISOString()
            })
                .eq('id', goalId);
            if (error) {
                console.error('Error updating goal position:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to update goal position:', error);
            return false;
        }
    },
    /**
     * Toggle goal expanded state
     */
    async toggleGoalExpanded(goalId, expanded) {
        try {
            const { error } = await supabase
                .from('goals')
                .update({
                canvas_expanded: expanded,
                updated_at: new Date().toISOString()
            })
                .eq('id', goalId);
            if (error) {
                console.error('Error toggling goal expanded:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to toggle goal expanded:', error);
            return false;
        }
    },
    /**
     * Create a relationship between two goals
     */
    async createGoalRelationship(parentGoalId, childGoalId, type = 'child') {
        try {
            const { data, error } = await supabase
                .from('goal_relationships')
                .insert({
                parent_goal_id: parentGoalId,
                child_goal_id: childGoalId,
                relationship_type: type
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating goal relationship:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to create goal relationship:', error);
            return null;
        }
    },
    /**
     * Delete a goal relationship
     */
    async deleteGoalRelationship(parentGoalId, childGoalId) {
        try {
            const { error } = await supabase
                .from('goal_relationships')
                .delete()
                .eq('parent_goal_id', parentGoalId)
                .eq('child_goal_id', childGoalId);
            if (error) {
                console.error('Error deleting goal relationship:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete goal relationship:', error);
            return false;
        }
    },
    /**
     * Get all children of a goal
     */
    async getGoalChildren(goalId) {
        try {
            const { data: relationships, error: relError } = await supabase
                .from('goal_relationships')
                .select('child_goal_id')
                .eq('parent_goal_id', goalId);
            if (relError)
                throw relError;
            if (!relationships || relationships.length === 0) {
                return [];
            }
            const childIds = relationships.map(r => r.child_goal_id);
            const { data, error } = await supabase
                .from('goals')
                .select('*')
                .in('id', childIds);
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch goal children:', error);
            return [];
        }
    },
    /**
     * Get parent of a goal
     */
    async getGoalParent(goalId) {
        try {
            const { data: relationship, error: relError } = await supabase
                .from('goal_relationships')
                .select('parent_goal_id')
                .eq('child_goal_id', goalId)
                .single();
            if (relError)
                throw relError;
            if (!relationship)
                return null;
            const { data, error } = await supabase
                .from('goals')
                .select('*')
                .eq('id', relationship.parent_goal_id)
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Failed to fetch goal parent:', error);
            return null;
        }
    },
    /**
     * Get top-level goals (no parent)
     */
    async getTopLevelGoals(filter) {
        try {
            const allGoals = await this.getStrategyGoals(filter);
            const { data: relationships } = await supabase
                .from('goal_relationships')
                .select('child_goal_id');
            const childIds = new Set(relationships?.map(r => r.child_goal_id) || []);
            return allGoals.filter(goal => !childIds.has(goal.id));
        }
        catch (error) {
            console.error('Failed to fetch top-level goals:', error);
            return [];
        }
    },
    /**
     * Auto-layout goals hierarchically
     */
    autoLayoutGoals(goals, relationships) {
        const positions = new Map();
        // Build adjacency list
        const parentMap = new Map();
        relationships.forEach(rel => {
            if (!parentMap.has(rel.parent_goal_id)) {
                parentMap.set(rel.parent_goal_id, []);
            }
            parentMap.get(rel.parent_goal_id).push(rel.child_goal_id);
        });
        // Find top-level goals (no parent)
        const childIds = new Set(relationships.map(r => r.child_goal_id));
        const topLevelGoals = goals.filter(g => !childIds.has(g.id));
        // Layout top-level goals horizontally
        const horizontalSpacing = 300;
        const verticalSpacing = 200;
        let x = 0;
        topLevelGoals.forEach((goal, index) => {
            positions.set(goal.id, { x: x + index * horizontalSpacing, y: 0 });
            // Recursively position children
            const children = parentMap.get(goal.id) || [];
            this.layoutHierarchy(goal.id, children, positions, parentMap, horizontalSpacing, verticalSpacing);
        });
        return positions;
    },
    /**
     * Helper to recursively layout goal hierarchy
     */
    layoutHierarchy(parentId, childIds, positions, parentMap, horizontalSpacing, verticalSpacing, depth = 1) {
        const parentPos = positions.get(parentId) || { x: 0, y: 0 };
        const childCount = childIds.length;
        const totalWidth = childCount * horizontalSpacing;
        childIds.forEach((childId, index) => {
            const x = parentPos.x - totalWidth / 2 + index * horizontalSpacing;
            const y = parentPos.y + verticalSpacing * depth;
            positions.set(childId, { x, y });
            // Recursively layout its children
            const grandChildren = parentMap.get(childId) || [];
            if (grandChildren.length > 0) {
                this.layoutHierarchy(childId, grandChildren, positions, parentMap, horizontalSpacing, verticalSpacing, depth + 1);
            }
        });
    }
};
